import React, { useState, type ReactNode } from 'react';
import { V4 } from '../tokens';
import { Button } from '../Button/Button';

export type WizardV4Step = {
  component: React.ReactNode;
  validate?: () => boolean;
  onSubmit?: () => void | Promise<void>;
  disableNextButton?: boolean;
  hideNextButton?: boolean;
  icon?: React.ReactNode;
};

export type WizardV4Props = {
  title: string;
  steps: WizardV4Step[];
  subtitle?: string;
  isLoading?: boolean;
  onFinish: () => void | Promise<void>;
  image?: string;
  /**
   * Paso en el que arranca el wizard (default 0). Se lee UNA vez al montar;
   * cambios posteriores no re-navegan. Se clampa a [0, steps.length - 1].
   * Útil para retomar la posición tras un reload derivándola de datos guardados.
   */
  initialStep?: number;
};

type VisualStepProps = {
  step: number;
  index: number;
  isActive: boolean;
  isDone: boolean;
  icon?: ReactNode;
};

const VisualStep = ({ step, index, isActive, isDone, icon }: VisualStepProps) => {
  const size = icon ? 48 : 36;

  let background: string = V4.grayLight;
  let color: string = V4.grayUltraDark;
  if (isDone) {
    background = V4.accentMint;
    color = V4.white;
  } else if (isActive) {
    background = V4.secondary;
    color = V4.white;
  }

  const content = isDone ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12.5l4.5 4.5L19 7.5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    icon || step
  );

  return (
    <div
      data-testid={`wizard-v4-step-${index}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background,
        color,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        fontWeight: 600,
        fontFamily: 'inherit',
        transition: 'background 160ms cubic-bezier(.4,0,.2,1), color 160ms cubic-bezier(.4,0,.2,1)',
        flexShrink: 0,
        zIndex: 1,
      }}
    >
      {content}
    </div>
  );
};

export const WizardV4 = ({ title, subtitle, steps, onFinish, isLoading = false, initialStep = 0 }: WizardV4Props) => {
  const [currentStep, setCurrentStep] = useState(() => Math.min(Math.max(initialStep, 0), steps.length - 1));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const total = steps.length;
  const step = steps[currentStep];
  const isLastStep = currentStep === total - 1;
  const showNextButton = !step.hideNextButton && !isLastStep;
  const showFinalizar = isLastStep && !step.hideNextButton;
  const validationOk = step.validate ? step.validate() : true;
  // `ctaBlocked` = razones de negocio (visual: botón gris). El submit en vuelo
  // NO dimea el botón — se comunica con el spinner de `loading` del Button V4.
  const ctaBlocked = isLoading || Boolean(step.disableNextButton) || !validationOk;
  const ctaDisabled = ctaBlocked || isSubmitting;
  const backDisabled = isLoading || isSubmitting;

  const handleSiguiente = async () => {
    if (ctaDisabled) return;
    setIsSubmitting(true);
    try {
      await step.onSubmit?.();
      setCurrentStep((prev) => Math.min(prev + 1, total - 1));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinalizar = async () => {
    if (ctaDisabled) return;
    setIsSubmitting(true);
    try {
      await step.onSubmit?.();
      await onFinish();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAnterior = () => {
    if (backDisabled) return;
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <div
      data-testid="wizard-v4-container"
      style={{
        background: V4.white,
        borderRadius: 10,
        padding: 24,
        boxShadow: '0 4px 20px rgba(216,220,221,.89)',
        maxWidth: 768,
        width: '100%',
        margin: '0 auto',
        fontFamily: 'inherit',
        color: V4.primary,
      }}
    >
      <section style={{ textAlign: 'center', marginBottom: 8 }}>
        <h2
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            color: V4.primary,
            lineHeight: 1.25,
          }}
        >
          {title}
        </h2>
        {subtitle ? (
          <h3
            style={{
              margin: '8px 0 0',
              fontSize: 14,
              fontWeight: 400,
              color: V4.grayUltraDark,
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </h3>
        ) : null}
      </section>

      <div
        style={{
          position: 'relative',
          margin: '20px 0 24px',
          paddingInline: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: total === 1 ? 'center' : 'space-between',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {steps.map(({ icon }, index) => (
            <VisualStep
              key={index}
              step={index + 1}
              index={index}
              icon={icon}
              isDone={index < currentStep}
              isActive={index === currentStep}
            />
          ))}
          {total > 1 ? (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: 2,
                background: V4.grayLight,
                transform: 'translateY(-50%)',
                zIndex: 0,
              }}
            />
          ) : null}
        </div>
      </div>

      <div>{step.component}</div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 16,
          marginTop: 24,
          flexWrap: 'wrap',
        }}
      >
        {currentStep > 0 ? (
          <Button
            data-testid="wizard-v4-cta-secondary"
            variant="outline"
            size="lg"
            disabled={backDisabled}
            onClick={handleAnterior}
          >
            Anterior
          </Button>
        ) : null}
        {showNextButton ? (
          <Button
            data-testid="wizard-v4-cta-primary"
            variant="primary"
            size="lg"
            disabled={ctaBlocked}
            loading={isSubmitting}
            onClick={handleSiguiente}
          >
            Siguiente
          </Button>
        ) : null}
        {showFinalizar ? (
          <Button
            data-testid="wizard-v4-cta-primary"
            variant="primary"
            size="lg"
            disabled={ctaBlocked}
            loading={isSubmitting}
            onClick={handleFinalizar}
          >
            Finalizar
          </Button>
        ) : null}
      </div>
    </div>
  );
};
